import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  ShoppingCart, 
  DollarSign, 
  Users, 
  Star, 
  Eye,
  Car,
  Shirt,
  Zap,
  Home,
  Gamepad2,
  Crown,
  Server
} from 'lucide-react';
import SEO from '../components/SEO';
import ShinyText from '../components/ShinyText';

const Market: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = [
    { id: 'all', label: 'Tümü', icon: ShoppingCart },
    { id: 'vehicles', label: 'Araçlar', icon: Car },
    { id: 'clothing', label: 'Kıyafetler', icon: Shirt },
    { id: 'weapons', label: 'Silahlar', icon: Zap },
    { id: 'properties', label: 'Mülkler', icon: Home },
    { id: 'accessories', label: 'Aksesuarlar', icon: Crown },
    { id: 'games', label: 'Oyunlar', icon: Gamepad2 }
  ];

  // Firebase'den ürünleri yükle
  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsQuery = query(collection(db, 'marketProducts'), orderBy('createdAt', 'desc'));
      const productsSnapshot = await getDocs(productsQuery);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
    } catch (error) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);


  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <>
      <SEO 
        title="Market - Oyun İçi Eşyalar ve Araçlar | Aedon Community" 
        description="Aedon Community FiveM Market - En kaliteli oyun içi eşyalar, araçlar, kıyafetler, silahlar ve mülkler. FiveM sunucularımızda kullanabileceğiniz premium ürünleri keşfedin ve satın alın." 
        keywords="FiveM Market, FiveM Eşyalar, FiveM Araçlar, FiveM Kıyafetler, FiveM Silahlar, FiveM Mülkler, Oyun İçi Market, Aedon Community Market, FiveM Türkiye Market, FiveM Shop, aedon community market, aedon community fivem market, aedon community fivem shop, aedon community fivem products, aedon community fivem items, aedon community fivem vehicles, aedon community fivem clothing, aedon community fivem weapons, aedon community fivem properties, aedon community fivem accessories, aedon community fivem games" 
        url="https://aedoncommunity.com/market"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-gaming  text-white mb-4">
            <ShinyText text="Oyun Ici Market" speed={5} className="text-primary-500" />
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            FiveM sunucularımızda kullanabileceğiniz en kaliteli eşyaları keşfedin ve satın alın.
          </p>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-dark-700 text-gray-300 hover:bg-dark-600 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{category.label}</span>
              </button>
            );
          })}
        </motion.div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Ürünler yükleniyor...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card hover:bg-dark-700 transition-all duration-300 overflow-hidden"
            >
              {/* Product Image */}
              <div className="relative h-48 mb-6">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-4 right-4 flex items-center space-x-2">
                  <div className="flex items-center text-yellow-400 bg-dark-800/80 px-2 py-1 rounded">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="ml-1 text-sm font-medium">{product.rating}</span>
                  </div>
                </div>
                <div className="absolute top-4 left-4">
                  <div className="flex items-center text-green-400 bg-dark-800/80 px-3 py-1 rounded">
                    <DollarSign className="w-4 h-4" />
                    <span className="ml-1 text-sm font-bold">{product.price.toLocaleString()} {product.currency}</span>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{product.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Server className="w-4 h-4 text-primary-400" />
                    <span className="text-sm text-gray-300">{product.server || 'Sunucu belirtilmemiş'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">{product.players} Oyuncu</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-gray-300">{product.price.toLocaleString()} {product.currency}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-gray-300">v{product.version}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Özellikler:</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-dark-600 text-gray-300 text-xs rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Author and Actions */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    Geliştirici: <span className="text-primary-400">{product.author}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button className="btn-secondary text-sm px-4 py-2">
                      <Eye className="w-4 h-4 mr-2" />
                      Önizleme
                    </button>
                    <button className="btn-primary text-sm px-4 py-2">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Satın Al
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredProducts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <ShoppingCart className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Bu kategoride ürün bulunamadı
            </h3>
            <p className="text-gray-500">
              Farklı bir kategori seçmeyi deneyin
            </p>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
};

export default Market; 